import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Full-page "this could not be established" surface, shared by the router
 * gating chain (ESCLOUD-288).
 *
 * It exists because an unproven absence must never be acted on: a guard that
 * cannot answer its own question shows this and offers a retry rather than
 * redirecting on a guess. Every caller therefore says the same three things in
 * the same shape — what is unknown, why that is not the same as an answer, and
 * the way out — so only the words differ.
 */
export function RetryPanel({
    title,
    description,
    onRetry,
    actions,
}: {
    title: string;
    description: ReactNode;
    /** Renders the default single Retry button. */
    onRetry?: () => void;
    /** Replaces the default button, for a screen with more than one recovery. */
    actions?: ReactNode;
}) {
    return (
        <div className="flex h-full flex-col items-center justify-center gap-4 py-24 text-center">
            <div>
                <h2 className="font-medium">{title}</h2>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
            </div>
            <div className="flex justify-center gap-3">
                {actions ??
                    (onRetry && (
                        <Button variant="outline" onClick={onRetry}>
                            Retry
                        </Button>
                    ))}
            </div>
        </div>
    );
}
